// @ts-strict-ignore
import upperFirst from 'lodash/upperFirst';
import type {
  Action, ReasonCodeGroup, SchemaInstance, ServicesByName,
  Threshold,
} from 'flow/store/engine/types';
import {
  actionHasRules, convertExpressionToString, getActionName, getActionTypeText,
  isActionOfType,
} from 'flow/service/actions';
import { parseBooleanString } from 'flow/service/util';
import { convertRuleToString } from 'flow/service/thresholds';
import Components from './components';
import { getOperationVerbInPastTense } from '../helpers';
import type { Operation } from '../../types';
import type { WorkflowState } from '~/dashboard/views/workflows/store/redux/state';
import { ActionType } from 'flow/store/engine/constants';
import type { FeatureFlags } from '~/helpers/typescript';


type ModifyDirection = 'from' | 'to';


function packageDataType(dataType: string) {
  return `Data Type: ${dataType}`;
}

function packageUseJQ(useJq: string) {
  return `JQ: ${upperFirst(useJq)}`;
}

function packageJQFunction(jqFunction: string) {
  return `JQ Formula: ${jqFunction}`;
}

function packageExpression(expressionAsString: string) {
  return `Expression: ${expressionAsString}`;
}

function packageDirection(direction: ModifyDirection) {
  return direction === 'from' ? 'From:' : 'To:';
}

function packagePrecedenceIndex(precedenceIndex: number) {
  return `Precedence: ${precedenceIndex}`;
}

function packageServiceWithoutRules(serviceAction: Action) {
  const serviceName = getActionName(serviceAction);
  return `Service ${serviceName} always runs`;
}

export default function createTemplates({ featureFlags }: {
  featureFlags: Partial<FeatureFlags>;
}) {
  return {
    ineligibleChange() {
      return Components.IneligibleChange();
    },
    actionSectionTitle(opts: {
      action: Action;
      operation: Operation;
    }) {
      const { action, operation } = opts;

      const actionTypeText = getActionTypeText(action);
      const actionName = isActionOfType(action, ActionType.EXIT)
        ? 'Exit Workflow'
        : getActionName(action);
      const operationSuffix = upperFirst(getOperationVerbInPastTense(operation));

      const content = `${actionTypeText} ${operationSuffix}: ${actionName}`;

      return Components.ActionSectionTitle(content);
    },
    actionRulesAsString(opts: {
      deps: {
        action: Action;
        actions: SchemaInstance['actions'];
        reasonCodeGroups: ReasonCodeGroup[];
        services: ServicesByName;
        thresholds: SchemaInstance['thresholds'];
      };
      format?: {
        direction?: ModifyDirection;
      };
    }) {
      const {
        deps: {
          actions,
          reasonCodeGroups,
          services,
          action,
          thresholds,
        },
        format: {
          direction,
        } = {},
      } = opts;

      const directionContent = direction && packageDirection(direction);
      const rulesAsString = convertRuleToString({
        actions,
        reasonCodeGroups,
        rules: action.rules,
        services,
        thresholds,
        featureFlags,
      });

      const content = [
        ...(direction ? [directionContent] : []),
        rulesAsString,
      ];
      return Components.ChangeContainer(content);
    },
    defaultOutcome(opts: {
      outcomeAction: Action;
    }) {
      const { outcomeAction } = opts;
      const outcomeName = getActionName(outcomeAction);

      return Components.ChangeContainer(`Else evaluations will be decisioned to ${outcomeName}`);
    },
    serviceAlwaysRuns(opts: {
      serviceAction: Action;
    }) {
      const { serviceAction } = opts;
      const serviceContent = packageServiceWithoutRules(serviceAction);
      return Components.ChangeContainer(serviceContent);
    },
    inputActionMetadata(opts: {
      deps: {
        inputAction: Action;
      };
      format?: {
        direction: ModifyDirection;
      };
    }) {
      const { deps: { inputAction: { input } }, format: { direction } = {} } = opts;

      const { dataType, jqFunction, useJq } = input;

      const dataTypeContent = packageDataType(dataType);
      const useJqContent = packageUseJQ(useJq);
      const jqFunctionContent = packageJQFunction(jqFunction);

      const directionContent = direction && packageDirection(direction);

      const content = [
        ...(directionContent ? [directionContent] : []),
        dataTypeContent,
        useJqContent,
        ...(parseBooleanString(useJq) ? [jqFunctionContent] : []),
      ];

      return Components.ChangeContainer(content);
    },
    outputActionMetadata(opts: {
      deps: {
        outputAction: Action;
        actions: WorkflowState['workflowData']['actions'];
        dataRoutes: WorkflowState['workflowData']['dataRoutes'];
      };
      format?: {
        direction: ModifyDirection;
      };
    }) {
      const {
        deps: {
          outputAction: { output }, actions, dataRoutes,
        },
        format: { direction } = {},
      } = opts;

      const {
        dataType, expression, useJq, jqFunction,
      } = output;

      const directionContent = direction && packageDirection(direction);
      const dataTypeContent = packageDataType(dataType);
      const useJqContent = packageUseJQ(useJq);
      const jqFunctionContent = packageJQFunction(jqFunction);

      const expressionAsString = convertExpressionToString({
        actions,
        dataRoutes,
        expression,
      });
      const expressionContent = packageExpression(expressionAsString);

      const content = [
        ...(directionContent ? [directionContent] : []),
        dataTypeContent,
        expressionContent,
        useJqContent,
        ...(parseBooleanString(useJq) ? [jqFunctionContent] : []),
      ];

      return Components.ChangeContainer(content);
    },
    actionName(opts: {
      action: Action;
    }) {
      const { action } = opts;
      const actionName = getActionName(action);
      return Components.ChangeContainer(actionName);
    },
    tagActionMetadata(opts: {
      deps: {
        action: Action;
        actions: Record<string, Action>;
        services: ServicesByName;
        reasonCodeGroups: ReasonCodeGroup[];
        thresholds: Record<string, Threshold>;
      };
      format: {
        direction: ModifyDirection;
      };
    }) {
      const {
        deps: {
          actions,
          reasonCodeGroups,
          services,
          action,
          thresholds,
        },
        format: {
          direction,
        } = {},
      } = opts;

      const directionContent = direction && packageDirection(direction);
      const rulesAsString = convertRuleToString({
        actions,
        reasonCodeGroups,
        rules: action.rules,
        services,
        thresholds,
        featureFlags,
      });

      const content = [
        ...(direction ? [directionContent] : []),
        rulesAsString,
      ];
      return Components.ChangeContainer(content);
    },
    outcomeActionMetadata(opts: {
      deps: {
        actions: SchemaInstance['actions'];
        outcomeAction: Action;
        reasonCodeGroups: ReasonCodeGroup[];
        services: ServicesByName;
        thresholds: SchemaInstance['thresholds'];
      };
      modifications: {
        rulesModified: boolean;
        outcomeMetadataModified: boolean;
      };
      format: {
        direction: ModifyDirection;
      };
    }) {
      const {
        modifications: {
          outcomeMetadataModified,
          rulesModified,
        },
        deps: {
          actions,
          outcomeAction,
          reasonCodeGroups,
          services,
          thresholds,
        },
        format: {
          direction,
        },
      } = opts;

      const directionContent = packageDirection(direction);
      const rulesAsStringContent = rulesModified && convertRuleToString({
        actions,
        reasonCodeGroups,
        rules: outcomeAction.rules,
        services,
        thresholds,
        featureFlags,
      });

      const precedenceIndexContent = outcomeMetadataModified && packagePrecedenceIndex(
        outcomeAction.outcome.precedenceIndex,
      );

      const content = [
        ...(directionContent ? [directionContent] : []),
        ...(rulesAsStringContent ? [rulesAsStringContent] : []),
        ...(precedenceIndexContent ? [precedenceIndexContent] : []),
      ];
      return Components.ChangeContainer(content);
    },
    serviceActionMetadata(opts: {
      deps: {
        actions: Record<string, Action>;
        reasonCodeGroups: ReasonCodeGroup[];
        serviceAction: Action;
        services: ServicesByName;
        thresholds: Record<string, Threshold>;
      };
      format: {
        direction: ModifyDirection;
      };
      modifications: {
        rulesModified: boolean;
      };
    }) {
      const {
        deps: {
          actions,
          reasonCodeGroups,
          services,
          thresholds,
          serviceAction,

        },
        format: {
          direction,
        },
        modifications: {
          rulesModified,
        },
      } = opts;

      const directionContent = packageDirection(direction);

      let rulesAsStringContent: string;
      if (rulesModified) {
        rulesAsStringContent = actionHasRules(serviceAction)
          ? convertRuleToString({
            actions,
            reasonCodeGroups,
            rules: serviceAction.rules,
            services,
            thresholds,
            featureFlags,
          })
          : packageServiceWithoutRules(serviceAction);
      }

      const content = [
        directionContent,
        ...(rulesModified ? [rulesAsStringContent] : []),
      ];

      return Components.ChangeContainer(content);
    },
    // this function will be better implemented once requirements are clearer
    matrixModelActionMetadata() {
      return Components.ChangeContainer('Changes made');
    },
  };
}
